import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Redirect } from 'react-router-dom';
import { Center, Spinner } from 'src/components/atoms';
import { handleErrorToast } from 'src/components/molecules';
import {
  useGetDeterminedCategoriesQuery,
  useGetOrganisationTagsQuery,
} from 'src/graphql';
import {
  useCase,
  useCaseDocuments,
  useQueryParams,
  useViewCaseAuditEntry,
} from 'src/hooks';
import { activeCategoriesUpdated } from 'src/reducers/Categories.slice';
import { setGlobalTags } from 'src/reducers/Tags.slice';
import { AppDispatch } from 'src/store';
import * as Types from '../../../types';
import {
  caseCategoriesUpdated,
  caseDataUpdated,
  caseDeterminedCategoriesUpdated,
  caseLinksUpdated,
  casePageVm,
  relationshipTypesUpdated,
  toggleMessagesVisible,
  viewerDataUpdated,
} from './Case.slice';
import { CaseDetailsLayout } from './CaseDetailsLayout';

export const Case = () => {
  const dispatch = useDispatch<AppDispatch>();
  const queryParams = useQueryParams();

  const caseId = queryParams.get('id') ?? '';
  const reduxCase = useSelector(casePageVm);
  const reduxLoading = reduxCase?.caseId !== caseId;

  const isLegacyMode = useSelector(
    (state: Types.State) => state.organisation?.isLegacyMode,
  );

  const {
    caseById,
    isLoading,
    isError,
    refetch,
    viewer,
    availableCategories,
    relationshipTypes,
    isSaving,
    setIsSaving,
    addCaseCategory,
    removeCaseCategory,
    caseLinks,
    qaService,
  } = useCase(caseId, reduxCase.caseSelectedTranslation);

  const { data: determinedCategories } = useGetDeterminedCategoriesQuery();

  useEffect(() => {
    dispatch(
      caseDeterminedCategoriesUpdated(
        determinedCategories?.getDeterminedCategories ?? [],
      ),
    );
  }, [dispatch, determinedCategories]);

  useEffect(() => {
    dispatch(relationshipTypesUpdated(relationshipTypes ?? []));
  }, [dispatch, relationshipTypes]);

  useEffect(() => {
    dispatch(caseDataUpdated(caseById));
  }, [dispatch, caseById]);

  useEffect(() => {
    dispatch(toggleMessagesVisible({ visible: false }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, caseId]);

  useEffect(() => {
    dispatch(activeCategoriesUpdated(availableCategories));
  }, [dispatch, availableCategories]);

  useEffect(() => {
    dispatch(caseCategoriesUpdated(availableCategories));
  }, [dispatch, availableCategories]);

  useViewCaseAuditEntry(caseId, caseById);

  const { data: tags } = useGetOrganisationTagsQuery();
  useEffect(() => {
    if (tags) {
      dispatch(setGlobalTags(tags?.getOrganisationTags));
    }
  }, [dispatch, tags]);

  useEffect(() => {
    dispatch(caseLinksUpdated(caseLinks ?? []));
  }, [dispatch, caseLinks]);

  useEffect(() => {
    dispatch(viewerDataUpdated(viewer ?? null));
  }, [dispatch, viewer]);

  const caseDocuments = useCaseDocuments(caseId, refetch, setIsSaving);

  if (reduxCase.redirect || isError) {
    handleErrorToast({ toastMessage: 'Invalid case id' });
    return <Redirect to="/cases" />;
  }

  if (isLoading || reduxLoading || !caseById || !viewer) {
    return (
      <Center>
        <Spinner variant="secondary" size="xlarge" />
      </Center>
    );
  }

  if (
    caseById &&
    caseById.DisclosuresCaseData?.importType === Types.CaseTypeEnum.COI
  ) {
    return <Redirect to={`/disclosure?id=${caseId}`} />;
  }

  const showDynamicQuestions = !isLegacyMode && Boolean(qaService.qaResponseId);

  const categories = caseById.Categories ?? [];

  return (
    <CaseDetailsLayout
      caseType={Types.CaseTypeEnum.SPEAK_UP}
      caseId={caseId}
      caseById={caseById}
      viewer={viewer}
      refetch={refetch}
      addCaseCategory={addCaseCategory.mutate}
      removeCaseCategory={removeCaseCategory.mutate}
      availableCategories={availableCategories}
      categories={categories}
      caseDocuments={caseDocuments}
      isSaving={isSaving}
      setIsSaving={setIsSaving}
      showDynamicQuestions={showDynamicQuestions}
      qaServiceData={qaService}
    />
  );
};
